import { logger } from '../lib/logger';
import React from 'react';
import { ShieldCheck, Lock, Wallet, Scale, FileSearch, RefreshCw, ArrowRight } from 'lucide-react';
import TelegramButton from './TelegramButton';
import { Button } from './ui/button';
import { useQualification } from '../contexts/QualificationContext';

/**
 * "Cosa rischi? Niente." — risk-reversal block
 * Lists the legitimate guarantees / no-strings claims that don't require
 * unverified marketing language. Designed to defuse objections right before
 * the conversion section.
 */
const ITEMS = [
  {
    icon: ShieldCheck,
    title: 'Nessun anticipo, nessun impegno',
    body: 'Compili il modulo, ricevi le brochure e il project financing. Decidi tu se andare avanti, quando andare avanti, senza pressioni o urgenze artificiose.',
  },
  {
    icon: Lock,
    title: 'Dati protetti GDPR',
    body: 'Conformità GDPR completa. Niente spam, niente cessione a terzi, niente tracker pubblicitari. Disiscrivi con un click in fondo a ogni email.',
  },
  {
    icon: Wallet,
    title: 'Project financing trasparente',
    body: 'Numeri reali (CAPEX, OPEX, ricavi target, IRR atteso) consegnati in PDF prima di qualsiasi versamento. Niente forecast venduti come fatti.',
  },
  {
    icon: Scale,
    title: 'Contratto di compravendita chiaro',
    body: 'Atto di proprietà notarile, non promessa di rendimento. Compri l\'appartamento con piano pagamenti rateale, poi decidi tu se metterlo a reddito con il property management Green Paradise o usarlo personalmente.',
  },
  {
    icon: FileSearch,
    title: 'Verification Pass: vai sul posto',
    body: 'Prima di firmare puoi visitare il cantiere e le strutture partner del Verification Pass, accompagnato dal team HCF. Niente è "sulla fiducia": tocchi con mano sopralluoghi, masterplan, autorizzazioni.',
  },
  {
    icon: RefreshCw,
    title: 'Cambi idea? Nessun problema',
    body: 'Finché non firmi atto / contratto definitivo non c\'è alcun obbligo. Le brochure e il project financing restano tuoi, anche se decidi di non procedere.',
  },
];

export default function RiskReversalSection() {
  const { open: openQualifica } = useQualification();
  // Soft-lock guard for persuasive landing: if Form 1 not done OR VSL not
  // watched at 75%, scroll back to the hero video instead of opening Form 2.
  const handleCta = (source) => {
    let form1Done = false;
    let vslDone = false;
    try {
      form1Done = !!localStorage.getItem('gp_vsl_phase1');
      vslDone = localStorage.getItem('gp_vsl_unlocked') === '1';
    } catch (err) { logger.debug('RiskReversal best-effort op failed', err); }
    // No Form 1 → open Form 1 popup + scroll to video
    if (!form1Done) {
      try { window.dispatchEvent(new CustomEvent('gp:force-form1')); } catch (err) { logger.debug('RiskReversal best-effort op failed', err); }
      try {
        const el = document.querySelector('[data-testid="vsl-gate"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (err) { logger.debug('RiskReversal best-effort op failed', err); }
      return;
    }
    // Form 1 done but video < 75 % → scroll back so visitor finishes the video
    if (!vslDone) {
      try {
        const el = document.querySelector('[data-testid="vsl-gate"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (err) { logger.debug('RiskReversal best-effort op failed', err); }
      return;
    }
    openQualifica(source);
  };

  return (
    <section className="px-4 md:px-12 py-16 md:py-24 bg-[var(--surface-2,#f6f3ec)]" data-testid="risk-reversal-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-[var(--gp-gold)] font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" />
            Tranquillità
          </div>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-[3.5rem] leading-[1.05] mt-3 md:mt-4 text-[var(--gp-forest)]">
            Cosa rischi? <span className="italic text-[var(--gp-gold)]">Niente.</span>
          </h2>
          <p className="text-sm md:text-base text-[var(--gp-forest)]/70 mt-4 max-w-2xl mx-auto leading-relaxed">
            Capisco che parliamo di importi seri. Ecco perché abbiamo costruito ogni passaggio in modo che tu possa fermarti, valutare, riflettere — senza sorprese, senza pressioni, senza impegni nascosti.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {ITEMS.map((it, idx) => {
            const Icon = it.icon;
            return (
              <div
                key={it.title}
                className="group relative bg-[var(--surface-1)] rounded-xl p-5 md:p-6 border border-[var(--gp-forest)]/10 hover:border-[var(--gp-gold)]/40 hover:shadow-lg transition-all duration-200"
                data-testid={`risk-reversal-item-${idx}`}
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--gp-gold)]/12 text-[var(--gp-gold)] mb-3 group-hover:bg-[var(--gp-gold)]/20 transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-lg md:text-[1.3rem] text-[var(--gp-forest)] leading-snug">
                  {it.title}
                </h3>
                <p className="text-[13px] md:text-sm text-[var(--gp-forest)]/70 mt-2 leading-relaxed">
                  {it.body}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-10 md:mt-12 text-center">
          {/* Primary CTA — soft-locked to gate (Form 1 + 75% video) */}
          <Button
            size="lg"
            onClick={() => handleCta('risk_reversal')}
            className="!h-12 !px-7 !text-base bg-[var(--gp-gold)] !text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)] font-semibold shadow-[0_0_40px_-10px_var(--gp-gold)]"
            data-testid="risk-reversal-cta-primary"
          >
            Parla con un Senior Associate <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>

          <p className="mt-6 text-[11px] md:text-xs tracking-[0.18em] uppercase text-[var(--gp-forest)]/55">
            Hai una domanda specifica?
          </p>
          <div className="mt-3 flex items-center justify-center gap-3 flex-wrap">
            <TelegramButton variant="solid" size="md" label="Scrivici su Telegram" source="risk-reversal" />
            <span className="text-[11px] tracking-[0.18em] uppercase text-[var(--gp-forest)]/45">oppure email</span>
            <a href="mailto:greenparadise@housecashflows.com" className="text-[var(--gp-gold)] hover:underline text-sm">
              greenparadise@housecashflows.com
            </a>
          </div>
          <p className="text-[10px] text-[var(--gp-forest)]/45 mt-3">
            Risponde Richard o il Team HCF Property · risposta entro 1–2 ore lavorative
          </p>
        </div>
      </div>
    </section>
  );
}
